import { useTranslation } from 'react-i18next';

import type { PlanName } from '../../../api/types';
import { Button } from '../../../shared/ui/Button';
import { formatBillingDate } from '../lib/billingState';
import styles from './PaymentStatusBanner.module.css';

export type PaymentBannerKind = 'paid' | 'pending' | 'timeout' | 'failed';

interface PaymentStatusBannerProps {
  kind: PaymentBannerKind;
  /** Оплаченный тариф; null — оплата не запускалась (kind='failed' до шита). */
  targetPlan: PlanName | null;
  /** Действующий тариф и его срок — строка AC-14a о старом тарифе. */
  currentPlan: PlanName;
  currentExpiresAt: string | null;
  onRefresh: () => void;
  onRetry: () => void;
}

// PARA-131 (AC-6/7/9/14a, AC-E3) — «оплата принята» ≠ «подписка активна».
// Формулировка «оплата не прошла» при списанных деньгах — самый дорогой дефект
// этой story, поэтому таймаут показывается нетревожно и повтор оплаты не
// предлагается.
export function PaymentStatusBanner({
  kind,
  targetPlan,
  currentPlan,
  currentExpiresAt,
  onRefresh,
  onRetry,
}: PaymentStatusBannerProps): JSX.Element {
  const { t, i18n } = useTranslation('billing');

  // AC-14a: во время смены тарифа пользователь обязан видеть, что старый тариф
  // ещё действует — умолчание здесь читается как потеря доступа. Покупка с
  // FREE сменой тарифа не является: терять там нечего.
  const isPlanChange = targetPlan !== null && targetPlan !== currentPlan && currentPlan !== 'FREE';
  const keepsAccess =
    isPlanChange && currentExpiresAt !== null
      ? t('payment.keepsAccess', {
          plan: t(`plans.${currentPlan}.name`),
          date: formatBillingDate(currentExpiresAt, i18n.language),
        })
      : null;

  return (
    <section className={styles.banner} data-testid="payment-status-banner">
      <p className={styles.text}>
        {kind === 'paid' &&
          (isPlanChange
            ? t('payment.paidSwitch', { plan: t(`plans.${targetPlan}.name`) })
            : t('payment.paid'))}
        {kind === 'pending' && t('payment.pending')}
        {kind === 'timeout' && t('payment.timeout')}
        {kind === 'failed' && t('payment.failed')}
      </p>
      {keepsAccess !== null && kind !== 'failed' && (
        <p className={styles.note} data-testid="keeps-access">
          {keepsAccess}
        </p>
      )}
      {kind === 'timeout' && (
        <Button variant="secondary" fullWidth onClick={onRefresh}>
          {t('payment.refresh')}
        </Button>
      )}
      {kind === 'failed' && (
        <Button variant="secondary" fullWidth onClick={onRetry}>
          {t('payment.tryAgain')}
        </Button>
      )}
    </section>
  );
}
